import React, { Fragment, useState, useRef, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import axios from 'axios';
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { Dialog, Transition } from '@headlessui/react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { ToastContext } from '../../../components/Toast';

const customerDetailsSchema = z.object({
  firstName: z.string().min(2, 'First name is required.'),
  lastName: z.string().min(2, 'Last name is required.'),
  companyName: z.string().min(2, 'Company name is required.'),
  email: z.string().email('Please enter a valid email.'),
  phone: z.string().min(10, 'Please enter a valid phone number.'),
  locationsCount: z.coerce.number().int().min(1, 'Please enter at least one location.'),
});

const submitCustomerDetails = async (formData) => {
  const { data } = await axios.post(`${import.meta.env.VITE_API_URL}api/v4/locachecker/startProcessing`, formData);
  
  return data;
};

const CustomerDetailsModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [hCaptchaToken, setHCaptchaToken] = useState(null);
  
  const captchaRef = useRef(null);
  
  const { addToast } = useContext(ToastContext);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(customerDetailsSchema),
  });
  
  const { mutate, isPending: isSubmitting, isSuccess, isError, error } = useMutation({
    mutationFn: submitCustomerDetails,
  });
  
  useEffect(() => {
    if (isSuccess) {
      setIsModalOpen(false);
    }
    
    if (isError) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        addToast('You have made too many search requests. Please wait a few minutes and try again.');
      } else {
        addToast('An unexpected error occurred during submitting the form.');
      }
      
      captchaRef.current?.resetCaptcha();
      
      setHCaptchaToken(null);
      
      navigate('/')
    }
  }, [isSuccess, isError, error, navigate]);
  
  const onFinalSubmit = (data) => {
    const finalPayload = {
      placeId: location.state?.placeId,
      captchaToken: hCaptchaToken,
      ...data,
    };
    
    mutate(finalPayload);
  };
  
  return (
    <Transition appear show={isModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {}}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/30"/>
        </Transition.Child>
        
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel
                className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  Almost there! Just a few more details.
                </Dialog.Title>
                
                <div className="mt-4">
                  <form onSubmit={handleSubmit(onFinalSubmit)} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name</label>
                        <input type="text" id="firstName" className="input-field" {...register('firstName')} />
                        {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName.message}</p>}
                      </div>
                      
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label>
                        <input type="text" id="lastName" className="input-field" {...register('lastName')} />
                        {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName.message}</p>}
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700">Company Name</label>
                      <input type="text" id="company" className="input-field" {...register('companyName')} />
                      {errors.companyName && <p className="text-red-600 text-sm mt-1">{errors.companyName.message}</p>}
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                      <input type="email" id="email" className="input-field" {...register('email')} />
                      {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Mobile or Cell Phone Number</label>
                      <input type="tel" id="phone" className="input-field" {...register('phone')} />
                      {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>}
                    </div>
                    
                    <div>
                      <label htmlFor="locations" className="block text-sm font-medium text-gray-700">Number of Locations</label>
                      <input type="number" id="locations" className="input-field" {...register('locationsCount')} />
                      {errors.locationsCount && <p className="text-red-600 text-sm mt-1">{errors.locationsCount.message}</p>}
                    </div>
                    
                    <div className="pt-2">
                      <HCaptcha
                        sitekey={import.meta.env.VITE_HCAPTCHA_SITEKEY}
                        onVerify={setHCaptchaToken}
                        ref={captchaRef}
                      />
                    </div>
                    
                    <div className="mt-6">
                      <button type="submit" className="btn-primary w-full disabled:bg-gray-400" disabled={isSubmitting || !hCaptchaToken}>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CustomerDetailsModal;
